/**
 * Maps human-readable keys to translation IDs for the /tools/bmi-calculator
 * SEO landing page. New content (not from Framer XLIFF), so keys carry the
 * `bmi_` prefix directly.
 *
 * The page is gated to CH + DE, in de/en/fr where the country offers them,
 * so `de-DE` is held to full parity on this namespace. `de-AT`, `en-NL` and
 * `nl` still exempt it in `SKIP_NAMESPACES_PER_LOCALE`
 * (scripts/lint-mirror-locales.ts) — translate + drop an exemption when the
 * page opens to that market.
 */
export const bmiCalculatorKeys = {
  // Metadata
  metaTitle: "bmi_metaTitle",
  metaDescription: "bmi_metaDescription",

  // Hero
  heading: "bmi_heading", // "BMI calculator"
  subheading: "bmi_subheading",

  // Calculator widget
  calcTitle: "bmi_calcTitle", // "Calculate your BMI"
  calcHeightLabel: "bmi_calcHeightLabel", // "Height"
  calcWeightLabel: "bmi_calcWeightLabel", // "Weight"
  calcUnitCm: "bmi_calcUnitCm", // "cm"
  calcUnitKg: "bmi_calcUnitKg", // "kg"
  calcSubmit: "bmi_calcSubmit",
  calcReset: "bmi_calcReset", // "Clear"
  calcResultLabel: "bmi_calcResultLabel", // "Your BMI"
  calcEmptyState: "bmi_calcEmptyState",
  calcOutOfRange: "bmi_calcOutOfRange",
  calcHealthyRangeLabel: "bmi_calcHealthyRangeLabel",
  calcHealthyRangeValue: "bmi_calcHealthyRangeValue", // ICU {low} {high} {unit}
  calcScaleCaption: "bmi_calcScaleCaption",
  calcDisclaimer: "bmi_calcDisclaimer",

  // Quiz hand-off, shown in the result panel once Calculate has run
  quizHeading: "bmi_quizHeading",
  quizBody: "bmi_quizBody",
  quizCta: "bmi_quizCta",

  // BMI categories (shared by the result card and the reference table)
  catUnderweight: "bmi_catUnderweight",
  catHealthy: "bmi_catHealthy",
  catOverweight: "bmi_catOverweight",
  catObesity1: "bmi_catObesity1",
  catObesity2: "bmi_catObesity2",
  catObesity3: "bmi_catObesity3",

  rangeUnderweight: "bmi_rangeUnderweight", // "Below 18.5"
  rangeHealthy: "bmi_rangeHealthy", // "18.5 to 24.9"
  rangeOverweight: "bmi_rangeOverweight", // "25.0 to 29.9"
  rangeObesity1: "bmi_rangeObesity1", // "30.0 to 34.9"
  rangeObesity2: "bmi_rangeObesity2", // "35.0 to 39.9"
  rangeObesity3: "bmi_rangeObesity3", // "40.0 and above"

  meaningUnderweight: "bmi_meaningUnderweight",
  meaningHealthy: "bmi_meaningHealthy",
  meaningOverweight: "bmi_meaningOverweight",
  meaningObesity1: "bmi_meaningObesity1",
  meaningObesity2: "bmi_meaningObesity2",
  meaningObesity3: "bmi_meaningObesity3",

  // What is BMI
  whatLabel: "bmi_whatLabel",
  whatHeading: "bmi_whatHeading",
  whatP1: "bmi_whatP1",
  whatP2: "bmi_whatP2",
  whatP3: "bmi_whatP3",

  // Formula
  formulaHeading: "bmi_formulaHeading",
  formulaIntro: "bmi_formulaIntro",
  formulaMetricValue: "bmi_formulaMetricValue",
  formulaMetricExample: "bmi_formulaMetricExample",

  // Category reference table
  tableHeading: "bmi_tableHeading",
  tableIntro: "bmi_tableIntro",
  tableColCategory: "bmi_tableColCategory",
  tableColRange: "bmi_tableColRange",
  tableColMeaning: "bmi_tableColMeaning",
  tableNote: "bmi_tableNote",

  // Limitations
  limitsLabel: "bmi_limitsLabel",
  limitsHeading: "bmi_limitsHeading",
  limitsIntro: "bmi_limitsIntro",
  limit1Title: "bmi_limit1Title",
  limit1Body: "bmi_limit1Body",
  limit2Title: "bmi_limit2Title",
  limit2Body: "bmi_limit2Body",
  limit3Title: "bmi_limit3Title",
  limit3Body: "bmi_limit3Body",
  limit4Title: "bmi_limit4Title",
  limit4Body: "bmi_limit4Body",

  // Waist tip
  waistHeading: "bmi_waistHeading",
  waistBody: "bmi_waistBody",
  waistTipTitle: "bmi_waistTipTitle",
  waistTipBody: "bmi_waistTipBody",

  // Beyond BMI
  beyondLabel: "bmi_beyondLabel",
  beyondHeading: "bmi_beyondHeading",
  beyondIntro: "bmi_beyondIntro",
  beyond1Title: "bmi_beyond1Title",
  beyond1Body: "bmi_beyond1Body",
  beyond2Title: "bmi_beyond2Title",
  beyond2Body: "bmi_beyond2Body",
  beyond3Title: "bmi_beyond3Title",
  beyond3Body: "bmi_beyond3Body",
  beyond4Title: "bmi_beyond4Title",
  beyond4Body: "bmi_beyond4Body",

  // Membership section
  memLabel: "bmi_memLabel",
  memHeading: "bmi_memHeading",
  memBody: "bmi_memBody",
  memFeature1: "bmi_memFeature1",
  memFeature2: "bmi_memFeature2",
  memFeature3: "bmi_memFeature3",
  memFeature4: "bmi_memFeature4",
  memFeature5: "bmi_memFeature5",
  memCtaPrimary: "bmi_memCtaPrimary",
  memCtaSecondary: "bmi_memCtaSecondary",
  memFootnote: "bmi_memFootnote",

  // FAQ fallback (used until page-tagged FAQs exist in the CMS)
  faqHeading: "bmi_faqHeading",
  faqQ1: "bmi_faqQ1",
  faqA1: "bmi_faqA1",
  faqQ2: "bmi_faqQ2",
  faqA2: "bmi_faqA2",
  faqQ3: "bmi_faqQ3",
  faqA3: "bmi_faqA3",
  faqQ4: "bmi_faqQ4",
  faqA4: "bmi_faqA4",
  faqQ5: "bmi_faqQ5",
  faqA5: "bmi_faqA5",
  faqQ6: "bmi_faqQ6",
  faqA6: "bmi_faqA6",

  // Closing CTA
  ctaHeading: "bmi_ctaHeading",
  ctaBody: "bmi_ctaBody",
  ctaPrimary: "bmi_ctaPrimary",
  ctaSecondary: "bmi_ctaSecondary",

  // Legal / medical
  disclaimer: "bmi_disclaimer",
} as const
